import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Form, Container, Row, Col, Card, Table, Button, Tabs, Tab } from 'react-bootstrap';
import GridPagination from '../components/GridPagination';

function DepotModal({ depot, onClose, onSave }) {
  const [formData, setFormData] = useState({
    depot_name: '',
    short_code: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    country: '',
    postcode: '',
    telephone: '',
    fax: '',
  });
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (depot) {
      setFormData({
        depot_name: depot.depot_name || '',
        short_code: depot.short_code || '',
        address_1: depot.address_1 || '',
        address_2: depot.address_2 || '',
        address_3: depot.address_3 || '',
        town: depot.town || '',
        country: depot.country || '',
        postcode: depot.postcode || '',
        telephone: depot.telephone || '',
        fax: depot.fax || '',
      });
    }
  }, [depot]);

  // Helper to strip non-numeric characters for phone fields
  const sanitizePhoneNumber = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Apply field-specific transformations
    if (name === 'depot_name') {
      processedValue = value.slice(0, 20);
    } else if (name === 'short_code') {
      processedValue = value.slice(0, 3).toUpperCase();
    } else if (name === 'postcode') {
      processedValue = value.toUpperCase();
    } else if (name === 'telephone' || name === 'fax') {
      processedValue = sanitizePhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');

    // Validation
    if (!formData.depot_name.trim()) {
      setModalError('Depot name is required');
      return;
    }
    if (!formData.short_code.trim()) {
      setModalError('Short code is required');
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      setModalError(error.response?.data?.error || 'Failed to save depot');
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{depot ? 'Edit Depot' : 'Add New Depot'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <Tabs defaultActiveKey="details" className="mb-3">
            <Tab eventKey="details" title="Details">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Depot Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="depot_name"
                      value={formData.depot_name}
                      onChange={handleChange}
                      maxLength={20}
                      placeholder="Max 20 characters"
                    />
                    <Form.Text className="text-muted">
                      {formData.depot_name.length}/20 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Short Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="short_code"
                      value={formData.short_code}
                      onChange={handleChange}
                      maxLength={3}
                      placeholder="3 char code"
                      style={{ textTransform: 'uppercase' }}
                    />
                    <Form.Text className="text-muted">
                      {formData.short_code.length}/3 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="address" title="Address &amp; Contact">
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_1"
                      value={formData.address_1}
                      onChange={handleChange}
                      placeholder="Address line 1"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_2"
                      value={formData.address_2}
                      onChange={handleChange}
                      placeholder="Address line 2"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 3</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_3"
                      value={formData.address_3}
                      onChange={handleChange}
                      placeholder="Address line 3"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Town</Form.Label>
                    <Form.Control
                      type="text"
                      name="town"
                      value={formData.town}
                      onChange={handleChange}
                      placeholder="Town"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Country"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      placeholder="Postcode"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telephone</Form.Label>
                    <Form.Control
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      placeholder="Numbers only"
                    />
                    <Form.Text className="text-muted">
                      Numbers only - characters will be removed
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fax</Form.Label>
                    <Form.Control
                      type="text"
                      name="fax"
                      value={formData.fax}
                      onChange={handleChange}
                      placeholder="Numbers only"
                    />
                    <Form.Text className="text-muted">
                      Numbers only - characters will be removed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {depot ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function Depots() {
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDepot, setEditingDepot] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchDepots = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/depots', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setDepots(response.data.depots);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch depots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepots();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDepots(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchDepots(newPage, searchTerm);
  };

  const handleAddDepot = () => {
    setEditingDepot(null);
    setShowModal(true);
  };

  const handleEditDepot = (depot) => {
    setEditingDepot(depot);
    setShowModal(true);
  };

  const handleSaveDepot = async (depotData) => {
    try {
      if (editingDepot) {
        await axios.put(`/api/depots/${editingDepot.id}`, depotData);
        setSuccess('Depot updated successfully');
      } else {
        await axios.post('/api/depots', depotData);
        setSuccess('Depot created successfully');
      }
      setShowModal(false);
      fetchDepots(pagination.page, searchTerm);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteDepot = async (id) => {
    if (window.confirm('Are you sure you want to delete this depot?')) {
      try {
        await axios.delete(`/api/depots/${id}`);
        setSuccess('Depot deleted successfully');
        fetchDepots(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete depot');
      }
    }
  };

  const handleDeleteAllDepots = async () => {
    if (
      window.confirm('Are you sure you want to delete ALL depots? This action cannot be undone!')
    ) {
      if (
        window.confirm(
          'This will permanently delete all your depot records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/depots');
          setSuccess(`All depots deleted successfully (${response.data.deletedCount} records)`);
          fetchDepots(1, '');
        } catch (error) {
          setError('Failed to delete all depots');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-warehouse me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0 h3">Depots</h1>
              <p className="text-muted mb-0 small">Manage your depot locations</p>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Form onSubmit={handleSearch} className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Search by name, code, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow-1"
                />
                <Button variant="primary" type="submit">
                  Search
                </Button>
                <Button variant="primary" onClick={handleAddDepot}>
                  Add Depot
                </Button>
                <Button variant="danger" onClick={handleDeleteAllDepots}>
                  Delete All
                </Button>
              </Form>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Short Code</th>
                        <th>Depot Name</th>
                        <th>Address</th>
                        <th>Town</th>
                        <th>Postcode</th>
                        <th>Telephone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {depots.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No depots found
                          </td>
                        </tr>
                      ) : (
                        depots.map((depot) => (
                          <tr key={depot.id}>
                            <td>
                              <strong>{depot.short_code}</strong>
                            </td>
                            <td>{depot.depot_name}</td>
                            <td>
                              {[depot.address_1, depot.address_2, depot.address_3]
                                .filter(Boolean)
                                .join(', ')}
                            </td>
                            <td>{depot.town}</td>
                            <td>{depot.postcode}</td>
                            <td>{depot.telephone}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => handleEditDepot(depot)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteDepot(depot.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  {pagination.pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted small">
                        Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                        {pagination.total} depots
                      </div>
                      <GridPagination
                        page={pagination.page}
                        pages={pagination.pages}
                        onPageChange={handlePageChange}
                        size="sm"
                        ulClassName="mb-0"
                        wrapperClassName=""
                        aria-label="Depots pagination"
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showModal && (
        <DepotModal
          depot={editingDepot}
          onClose={() => setShowModal(false)}
          onSave={handleSaveDepot}
        />
      )}
    </Container>
  );
}

export default Depots;
